import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusChip from "../components/StatusChip";
import RescheduleDialog from "../components/RescheduleDialog";
import { ChevronLeft, ChevronRight } from "lucide-react";

const fmtDay = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
const iso = (d) => d.toISOString().slice(0, 10);
const tm = (s) => new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });

export default function CalendarView() {
  const [mode, setMode] = useState("day");
  const [anchor, setAnchor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [resched, setResched] = useState(null);
  const { user } = useAuth();
  const canManage = ["tdc", "sm", "admin"].includes(user.role);

  const days = useMemo(() => {
    if (mode === "day") return [new Date(anchor)];
    const start = new Date(anchor);
    start.setDate(start.getDate() - start.getDay());
    return [...Array(7)].map((_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  }, [mode, anchor]);

  const load = () => api.get("/calendar/home-td", { params: { date_from: iso(days[0]), date_to: iso(days[days.length - 1]) } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [days]);
  const cancel = async (b) => {
    if (!window.confirm(`Cancel ${b.reg_no} for ${b.customer_name}? KEC, SM and TDC will be alerted.`)) return;
    try { await api.post(`/bookings/${b.id}/release`); toast.success("Reservation cancelled"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const shift = (n) => { const d = new Date(anchor); d.setDate(d.getDate() + n * (mode === "day" ? 1 : 7)); setAnchor(d); };

  return (
    <div className="px-4 py-5 lg:px-8 max-w-4xl">
      <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Test Drive Calendar</h1>
      <p className="text-xs text-slate-500 mt-0.5">Home TDs, connected drives and advance-blocked showroom slots{canManage ? " — change or cancel slots here; SM & KEC are alerted" : ""}</p>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex rounded-xl bg-slate-100 p-1">
          {["day", "week"].map((m) => (
            <button key={m} data-testid={`calendar-mode-${m}`} onClick={() => setMode(m)}
              className={`rounded-lg px-4 py-1.5 text-sm font-bold capitalize ${mode === m ? "bg-[#0E2240] text-white" : "text-slate-500"}`}>{m}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" onClick={() => shift(-1)} className="rounded-lg border border-slate-200 bg-white p-2"><ChevronLeft size={16} /></button>
          <button data-testid="calendar-today" onClick={() => setAnchor(new Date())} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold">Today</button>
          <button data-testid="calendar-next" onClick={() => shift(1)} className="rounded-lg border border-slate-200 bg-white p-2"><ChevronRight size={16} /></button>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {days.map((d) => {
          const dayItems = items.filter((b) => b.slot_start?.slice(0, 10) === iso(d));
          return (
            <div key={iso(d)}>
              <div className="text-sm font-bold text-[#0E2240]">{fmtDay(d)} <span className="text-slate-300 font-normal">· {dayItems.length} slots</span></div>
              <div className="mt-1.5 space-y-2">
                {dayItems.length === 0 && <div className="rounded-lg border border-dashed border-slate-200 p-3 text-center text-xs text-slate-300">No slots scheduled</div>}
                {dayItems.map((b) => (
                  <div key={b.id} data-testid={`calendar-item-${b.reg_no}`} className={`rounded-xl border p-3 text-sm ${b.status === "slot_missed" ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-[#0E2240]">{tm(b.slot_start)}–{tm(b.slot_end)} · {b.reg_no} ({b.model}) <span className="text-[10px] font-bold uppercase text-slate-400">{b.type === "home" ? "Home" : b.type === "connected" ? "Connected" : "Showroom · advance"}</span></div>
                        <div className="text-[11px] text-slate-500">{b.kec_name} → {b.customer_name}{b.expected_km ? ` · ${b.expected_km} km` : ""}{b.address ? ` · ${b.address}` : ""}</div>
                        {b.status === "slot_missed" && <div className="text-[11px] font-semibold text-red-700 mt-0.5">{b.slot_missed_reason} — re-slot needed</div>}
                        {b.rescheduled_by && <div className="text-[10px] text-slate-400">Rescheduled by {b.rescheduled_by}</div>}
                      </div>
                      <StatusChip status={b.status} />
                    </div>
                    {canManage && !["completed", "released"].includes(b.status) && (
                      <div className="mt-2 flex gap-2">
                        <button data-testid={`calendar-reschedule-${b.reg_no}`} onClick={() => setResched(b)}
                          className="rounded-lg border border-[#0E2240] px-3 py-1.5 text-[11px] font-bold text-[#0E2240]">Change slot</button>
                        <button data-testid={`calendar-cancel-${b.reg_no}`} onClick={() => cancel(b)}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-[11px] font-bold text-slate-600">Cancel</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {resched && <RescheduleDialog booking={resched} onClose={() => setResched(null)} onDone={load} />}
    </div>
  );
}
